import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import AuthShell from "../components/auth/AuthShell";
import PasswordField from "../components/auth/PasswordField";
import { authService } from "../services/authService";

type Phase = "checking" | "form" | "invalid" | "done";

/** Postadaki bağlantıdan gelinen sayfa: token doğrulanır, yeni şifre alınır. */
const ResetPassword: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [phase, setPhase] = useState<Phase>("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Formu göstermeden önce token'ı sor — süresi dolmuş bağlantıda kullanıcı
  // boşuna şifre yazmasın.
  useEffect(() => {
    let alive = true;
    if (!token) { setPhase("invalid"); return; }
    authService
      .validateResetToken(token)
      .then((ok) => { if (alive) setPhase(ok ? "form" : "invalid"); })
      .catch(() => { if (alive) setPhase("invalid"); });
    return () => { alive = false; };
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) return setError("Şifre en az 6 karakter olmalıdır.");
    if (password !== confirm) return setError("Şifreler eşleşmiyor.");

    setLoading(true);
    setError("");
    try {
      await authService.resetPassword(token, password);
      setPhase("done");
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.code === "EXPIRED" || data?.code === "USED" || data?.code === "INVALID") {
        setPhase("invalid");
        setError(data.message);
      } else {
        setError(data?.message || "Şifre güncellenemedi. Yeniden deneyin.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (phase === "checking") {
    return (
      <AuthShell title="Bağlantı denetleniyor…">
        <div className="lg-center-note">
          <span className="lg-spinner dark" aria-hidden="true"></span>
          <span>Sıfırlama bağlantınız doğrulanıyor.</span>
        </div>
      </AuthShell>
    );
  }

  if (phase === "invalid") {
    return (
      <AuthShell
        title="Bağlantı geçersiz"
        backTo={{ to: "/login", label: "Giriş ekranına dön" }}
      >
        <div className="lg-notice warn" role="alert">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
          <div>
            {error || "Bu sıfırlama bağlantısı geçersiz, süresi dolmuş veya daha önce kullanılmış."}
            <p>Bağlantılar 60 dakika geçerlidir ve yalnızca bir kez kullanılabilir.</p>
          </div>
        </div>

        <Link className="lg-submit as-link" to="/forgot-password">
          Yeni Bağlantı İste
        </Link>
      </AuthShell>
    );
  }

  if (phase === "done") {
    return (
      <AuthShell title="Şifreniz güncellendi">
        <div className="lg-notice ok" role="status">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="m8 12 3 3 5-6" />
          </svg>
          <div>
            Yeni şifrenizle giriş yapabilirsiniz.
            <p>Bu sıfırlama bağlantısı artık kullanılamaz.</p>
          </div>
        </div>

        <button type="button" className="lg-submit" onClick={() => navigate("/login")}>
          Giriş Yap
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Yeni Şifre Belirle"
      subtitle="Hesabınız için yeni bir şifre oluşturun"
      backTo={{ to: "/login", label: "Giriş ekranına dön" }}
    >
      <form onSubmit={submit} className="lg-form" noValidate>
        {error && (
          <div className="lg-error" role="alert">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <PasswordField
          id="newPassword"
          label="Yeni Şifre"
          value={password}
          onChange={(v) => { setPassword(v); setError(""); }}
          strength
          autoFocus
          hint="En az 6 karakter"
        />

        <PasswordField
          id="confirmPassword"
          label="Yeni Şifre (Tekrar)"
          value={confirm}
          onChange={(v) => { setConfirm(v); setError(""); }}
        />

        <button type="submit" className="lg-submit" disabled={loading}>
          {loading ? (
            <>
              <span className="lg-spinner" aria-hidden="true"></span>
              Kaydediliyor…
            </>
          ) : (
            "Şifreyi Güncelle"
          )}
        </button>
      </form>
    </AuthShell>
  );
};

export default ResetPassword;
